import { FC } from 'react';
import { Stack } from '@mui/material';
import { Modal, TextField } from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useCopyEmployeeGroup } from 'store/employee-groups';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import { routerHistory } from 'utils/router-history';
import { ROUTES } from 'config';

interface Values {
  name: string;
}

interface Props extends ModalProps {
  employeeGroupId: string;
  initialValues: {
    name: string;
  };
}

export const CopyEmployeeGroupModal: FC<Props> = ({
  employeeGroupId,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { handleSubmit, control } = useForm<Values>({
    defaultValues: {
      name: `${initialValues.name} (${t('common.copy')})`,
    },
  });

  const { mutate: copyEmployeeGroup, isLoading } = useCopyEmployeeGroup();

  const onSubmit: SubmitHandler<Values> = (values) => {
    copyEmployeeGroup(
      { employeeGroupId, name: values.name },
      {
        onSuccess: ({ data }) => {
          predefinedToasts.success.added();
          routerHistory.push(
            ROUTES.employeeGroups.details.replace(':id', data.id),
          );
          closeModal();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('groups.create_group_name')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isLoading,
          },
        ]}
      >
        <Stack gap="20px">
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('groups.group_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
